import { forwardRef, useMemo } from "react";

import { useSelectState } from "./hooks/useSelectState";
import { useMultiSelectState } from "./hooks/useMultiSelectState";
import { SelectFieldWrapper } from "./SelectFieldWrapper";
import { SelectCombobox } from "./SelectCombobox";
import { MultiSelectCombobox } from "./MultiSelectCombobox";
import type { SelectProps, MultiSelectProps, SelectOptionBase } from "./types";
import { normalizeOption, normalizeOptions } from "./utils";

export type {
  SelectProps,
  SelectOption,
  SelectOptionBase,
  SelectOptionGroup,
} from "./types";

type SingleSelectProps = Extract<SelectProps, { isMulti?: false }>;

const SingleSelectInner = forwardRef<HTMLInputElement, SingleSelectProps>(
  (props, ref) => {
    const {
      id,
      label,
      error,
      helpText,
      errorId,
      helpTextId,
      isDisabled,
      required,
      testIdPrefix,
      effectiveItems,
      labelForValue,
      renderOption,
      renderTrigger,
      isSearchable,
      isAsync,
      creatableFilter,
      isControlled,
      currentValue,
      handleValueChange,
      handleInputValueChange,
      handleClear,
      autoHighlight,
      showClearButton,
      sizeConfig,
      ariaDescribedBy,
      resolvedOptions,
      flatOptionsMap,
      isGrouped,
      isLoading,
      showCreateOption,
      hasMoreLazy,
      loaderCallbackRef,
    } = useSelectState(props);

    return (
      <SelectFieldWrapper
        id={id}
        label={label}
        error={error}
        helpText={helpText}
        errorId={errorId}
        helpTextId={helpTextId}
        isDisabled={isDisabled}
        required={required}
        testIdPrefix={testIdPrefix}
        labelProps={props.labelProps}
        className={props.className}
      >
        <SelectCombobox
          ref={ref}
          name={props.name}
          id={id}
          effectiveItems={effectiveItems}
          labelForValue={labelForValue}
          renderOption={renderOption}
          renderTrigger={renderTrigger}
          isSearchable={isSearchable}
          isAsync={isAsync}
          isCreatable={props.isCreatable ?? false}
          creatableFilter={creatableFilter}
          isControlled={isControlled}
          valueProp={props.value}
          defaultValue={props.defaultValue}
          currentValue={currentValue}
          handleValueChange={handleValueChange}
          handleInputValueChange={handleInputValueChange}
          handleClear={handleClear}
          onOpenChange={props.onOpenChange}
          isDisabled={isDisabled}
          autoHighlight={autoHighlight}
          required={required}
          isClearable={props.isClearable ?? false}
          showClearButton={showClearButton}
          sizeConfig={sizeConfig}
          error={error}
          placeholder={props.placeholder ?? ""}
          ariaDescribedBy={ariaDescribedBy}
          testIdPrefix={testIdPrefix}
          inputClassName={props.inputClassName}
          contentProps={props.contentProps}
          dropdownProps={{
            resolvedOptions,
            flatOptionsMap,
            isGrouped,
            isLoading,
            loadingMessage: props.loadingMessage ?? "Loading\u2026",
            emptyMessage: props.emptyMessage ?? "No results found.",
            showCreateOption,
            hasMoreLazy,
            loaderCallbackRef,
          }}
        >
          {props.children}
        </SelectCombobox>
      </SelectFieldWrapper>
    );
  }
);
SingleSelectInner.displayName = "SingleSelectInner";

const MultiSelectInner = forwardRef<HTMLInputElement, MultiSelectProps>(
  (props, ref) => {
    const {
      id,
      label,
      error,
      helpText,
      errorId,
      helpTextId,
      testIdPrefix,
      effectiveItems,
      labelForValue,
      renderOption,
      isAsync,
      creatableFilter,
      isControlled,
      selectedValues,
      handleValueChange,
      handleInputValueChange,
      sizeConfig,
      ariaDescribedBy,
      resolvedOptions,
      flatOptionsMap,
      isGrouped,
      isLoading,
      showCreateOption,
      hasMoreLazy,
      loaderCallbackRef,
    } = useMultiSelectState(props);
    const { isDisabled = false, required = false } = props;

    return (
      <SelectFieldWrapper
        id={id}
        label={label}
        error={error}
        helpText={helpText}
        errorId={errorId}
        helpTextId={helpTextId}
        isDisabled={isDisabled}
        required={required}
        testIdPrefix={testIdPrefix}
        labelProps={props.labelProps}
        className={props.className}
      >
        <MultiSelectCombobox
          ref={ref}
          name={props.name}
          id={id}
          effectiveItems={effectiveItems}
          labelForValue={labelForValue}
          renderOption={renderOption}
          isSearchable={props.isSearchable ?? true}
          isAsync={isAsync}
          isCreatable={props.isCreatable ?? false}
          creatableFilter={creatableFilter}
          isControlled={isControlled}
          valueProp={props.value}
          defaultValue={props.defaultValue}
          selectedValues={selectedValues}
          handleValueChange={handleValueChange as (...args: unknown[]) => void}
          handleInputValueChange={
            handleInputValueChange as (...args: unknown[]) => void
          }
          onOpenChange={props.onOpenChange}
          isDisabled={isDisabled}
          autoHighlight={props.autoHighlight ?? true}
          sizeConfig={sizeConfig}
          error={error}
          chipsClassName={props.chipsClassName}
          inputClassName={props.inputClassName}
          placeholder={props.placeholder ?? ""}
          hideChipRemove={props.hideChipRemove ?? false}
          ariaDescribedBy={ariaDescribedBy}
          required={required}
          testIdPrefix={testIdPrefix}
          contentProps={props.contentProps}
          dropdownProps={{
            resolvedOptions,
            flatOptionsMap,
            isGrouped,
            isLoading,
            loadingMessage: props.loadingMessage ?? "Loading\u2026",
            emptyMessage: props.emptyMessage ?? "No results found.",
            showCreateOption,
            hasMoreLazy,
            loaderCallbackRef,
          }}
        >
          {props.children}
        </MultiSelectCombobox>
      </SelectFieldWrapper>
    );
  }
);
MultiSelectInner.displayName = "MultiSelectInner";

const Select = forwardRef<HTMLInputElement, SelectProps>((props, ref) => {
  const {
    getOptionValue,
    getOptionLabel,
    options,
    defaultOptions,
    loadOptions,
    ...rest
  } = props;
  const hasAccessors = Boolean(getOptionValue || getOptionLabel);

  const normalizedOptions = useMemo(
    () =>
      hasAccessors
        ? normalizeOptions(options, getOptionValue, getOptionLabel)
        : options,
    [hasAccessors, options, getOptionValue, getOptionLabel]
  );

  const normalizedDefaultOptions = useMemo(() => {
    if (!hasAccessors || !Array.isArray(defaultOptions)) {
      return defaultOptions;
    }
    return defaultOptions.map(o =>
      normalizeOption(o, getOptionValue, getOptionLabel)
    );
  }, [hasAccessors, defaultOptions, getOptionValue, getOptionLabel]);

  const normalizedLoadOptions = useMemo(() => {
    if (!hasAccessors || !loadOptions) return loadOptions;
    return async (input: string): Promise<SelectOptionBase[]> => {
      const result = await loadOptions(input);
      return result.map(o =>
        normalizeOption(o, getOptionValue, getOptionLabel)
      );
    };
  }, [hasAccessors, loadOptions, getOptionValue, getOptionLabel]);

  const nextProps = {
    ...rest,
    options: normalizedOptions,
    defaultOptions: normalizedDefaultOptions,
    loadOptions: normalizedLoadOptions,
  } as SelectProps;

  if (nextProps.isMulti === true) {
    return <MultiSelectInner {...(nextProps as MultiSelectProps)} ref={ref} />;
  }
  return <SingleSelectInner {...(nextProps as SingleSelectProps)} ref={ref} />;
});

Select.displayName = "Select";

export { Select };
